import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useEffect, useRef, useState, useCallback } from 'react';
import { trpc } from '../../../trpc';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { LoaderCircle } from 'lucide-react';
import type { Application, Contact, Note } from 'types';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../../components/ui/tabs';
import { ApplicationHeader } from '../../../components/ApplicationHeader';
import { ApplicationEditForm } from '../../../components/ApplicationEditForm';
import { ApplicationViewMode } from '../../../components/ApplicationViewMode';
import { ContactsTab } from '../../../components/ContactsTab';
import { NotesTab } from '../../../components/NotesTab';
import { UnsavedChangesDialog } from '../../../components/UnsavedChangesDialog';
import { ContactDialog } from '../../../components/ContactDialog';
import { NoteDialog } from '../../../components/NoteDialog';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { isSystemGeneratedNote } from '../../../components/SystemNotesOnlyUI';
import { useTranslation } from 'react-i18next';

// Maps the `from` search param (where the user came from) to the dashboard tab
// the back button should return them to, plus the i18n key for its label.
// Deep links (no/unknown `from`) fall back to the feed.
const ORIGIN_TARGETS: Record<string, { path: string; labelKey: string }> = {
  applications: {
    path: '/dashboard/applications',
    labelKey: 'application.backToSavedJobs',
  },
  'suggested-jobs': {
    path: '/dashboard/suggested-jobs',
    labelKey: 'application.backToSuggestedJobs',
  },
  feed: { path: '/dashboard/feed', labelKey: 'application.backToFeed' },
  analytics: {
    path: '/dashboard/analytics',
    labelKey: 'application.backToAnalytics',
  },
};

// Helper function to wait for Clerk to load
const waitForClerk = async (maxAttempts = 50) => {
  for (let i = 0; i < maxAttempts; i++) {
    if (window.Clerk?.user !== undefined) {
      return window.Clerk.user;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return window.Clerk?.user;
};

export const Route = createFileRoute('/applications/$lookupId/')({
  component: ApplicationPage,
  beforeLoad: async () => {
    // Wait for Clerk to load
    const user = await waitForClerk();
    if (!user) {
      return redirect({
        to: '/',
        replace: true,
      });
    }
  },
});

function ApplicationPage() {
  const { lookupId } = Route.useParams();
  const { from, markApplied } = Route.useSearch() as {
    from?: string;
    markApplied?: string;
  };
  const navigate = useNavigate();
  const { user } = useUser();
  const userId = user?.id;
  const { t } = useTranslation();

  // Resolve where the back button returns the user, based on the tab they came
  // from. Deep links (no/unknown `from`) fall back to the feed.
  const backTarget = ORIGIN_TARGETS[from ?? ''] ?? ORIGIN_TARGETS.feed;

  // State for controlling dialogs
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editContactDialogOpen, setEditContactDialogOpen] = useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmNoteDialogOpen, setConfirmNoteDialogOpen] = useState(false);
  const [currentContact, setCurrentContact] = useState<Contact | undefined>(
    undefined
  );
  const [contactToDelete, setContactToDelete] = useState<Contact | undefined>(
    undefined
  );
  const [currentNote, setCurrentNote] = useState<Note | undefined>(undefined);
  const [noteToDelete, setNoteToDelete] = useState<Note | undefined>(undefined);
  const [showSystemNotes, setShowSystemNotes] = useState(false);
  const [initialNoteCheck, setInitialNoteCheck] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Application>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  // Get tRPC utils for cache invalidation
  const utils = trpc.useUtils();

  // Query for the application by lookup ID
  const applicationQuery = trpc.applications.getByLookupId.useQuery(
    { lookupId, userId },
    {
      enabled: !!userId && !!lookupId,
      retry: false,
      onError: (error: any) => {
        console.error('Error fetching application:', error);
        toast.error(t('application.errors.notFound'));
        setTimeout(() => {
          navigate({ to: backTarget.path as any, replace: true });
        }, 1000);
      },
    }
  );

  // Get the application contacts
  const contactsQuery = trpc.contacts.getByApplicationId.useQuery(
    { applicationId: applicationQuery.data?.id || 0 },
    {
      enabled: !!applicationQuery.data?.id,
      onError: (error: Error) => {
        console.error('Error fetching contacts:', error);
      },
    }
  );

  // Get the application notes
  const notesQuery = trpc.notes.getByApplicationId.useQuery(
    { applicationId: applicationQuery.data?.id || 0 },
    {
      enabled: !!applicationQuery.data?.id,
      onError: (error: Error) => {
        console.error('Error fetching notes:', error);
      },
      onSuccess: (data: Note[]) => {
        if (!initialNoteCheck && data && data.length > 0) {
          const hasUserNotes = data.some(
            (note: Note) => !isSystemGeneratedNote(note)
          );
          if (!hasUserNotes) {
            setShowSystemNotes(true);
          }
          setInitialNoteCheck(true);
        }
      },
    }
  );

  // Update application mutation
  const updateApplication = trpc.applications.update.useMutation({
    onSuccess: () => {
      toast.success(t('application.updateSuccess'));
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      utils.applications.getByLookupId.invalidate({ lookupId, userId });
    },
    onError: (error: unknown) => {
      console.error('Application update error:', error);
      toast.error(t('application.updateError'));
    },
  });

  // One-click "Mark as Applied" from the "Did you apply?" email. When the link
  // lands here with ?markApplied=1 and the job is still un-applied, flip it to
  // Applied using the user's existing session, then strip the param so a
  // refresh/back doesn't re-trigger it.
  const markAppliedHandled = useRef(false);
  useEffect(() => {
    if (!markApplied) return;
    const app = applicationQuery.data;
    if (!app || markAppliedHandled.current) return;
    markAppliedHandled.current = true;

    if (app.status?.toLowerCase() !== 'applied') {
      updateApplication.mutate({ id: app.id, status: 'applied' });
    }

    navigate({
      // This route declares no validateSearch schema, so TanStack types the
      // search-reducer's return as `never`. These params are read via a cast
      // (see Route.useSearch above); mirror that here to strip markApplied.
      search: (prev) => ({ ...prev, markApplied: undefined }) as never,
      replace: true,
    });
  }, [markApplied, applicationQuery.data, navigate, updateApplication]);

  // Delete contact mutation
  const deleteContact = trpc.contacts.delete.useMutation({
    onSuccess: () => {
      toast.success(t('contacts.deleteSuccess'));
      utils.contacts.getByApplicationId.invalidate({
        applicationId: applicationQuery.data?.id || 0,
      });
    },
    onError: (error: unknown) => {
      console.error('Contact deletion error:', error);
      toast.error(t('contacts.deleteError'));
    },
  });

  // Delete note mutation
  const deleteNote = trpc.notes.delete.useMutation({
    onSuccess: () => {
      toast.success(t('notes.deleteSuccess'));
      utils.notes.getByApplicationId.invalidate({
        applicationId: applicationQuery.data?.id || 0,
      });
    },
    onError: (error: unknown) => {
      console.error('Note deletion error:', error);
      toast.error(t('notes.deleteError'));
    },
  });

  // Edit mode handlers
  const handleEditClick = () => {
    if (applicationQuery.data) {
      setEditFormData({
        position: applicationQuery.data.position,
        company: applicationQuery.data.company,
        location: applicationQuery.data.location || '',
        status: applicationQuery.data.status,
        jobType: applicationQuery.data.jobType || '',
        salary: applicationQuery.data.salary,
        salaryCurrency: applicationQuery.data.salaryCurrency || 'USD',
        salaryTerm: applicationQuery.data.salaryTerm || 'none',
        urlSource: applicationQuery.data.urlSource || '',
        description: applicationQuery.data.description || '',
        notes: applicationQuery.data.notes || '',
        referenceNumber: applicationQuery.data.referenceNumber || '',
        appliedDate: applicationQuery.data.appliedDate,
      });
      setIsEditMode(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleEditFormChange = (
    field: string,
    value: string | number | Date | null
  ) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSaveClick = () => {
    if (applicationQuery.data && editFormData) {
      // `editFormData.notes` is seeded from the DTO's `.notes`, which is the
      // Note[] relation (not the string column). Only send it when the notes
      // textarea was actually edited into a string; otherwise omit it so we
      // don't ship an array to the string-typed `notes` field (Zod rejects it)
      // or clobber the existing value.
      const { notes, ...restFormData } = editFormData;
      const updateData = {
        id: applicationQuery.data.id,
        ...restFormData,
        ...(typeof notes === 'string' ? { notes } : {}),
        salary: editFormData.salary || undefined,
      };
      updateApplication.mutate(updateData);
    }
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesDialog(true);
    } else {
      setIsEditMode(false);
      setEditFormData({});
    }
  };

  const handleUnsavedChangesSave = () => {
    handleSaveClick();
    setShowUnsavedChangesDialog(false);
  };

  const handleUnsavedChangesDiscard = () => {
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    setEditFormData({});

    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
    setShowUnsavedChangesDialog(false);
  };

  // Navigation guard
  const handleNavigation = useCallback(
    (navFunction: () => void) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(() => navFunction);
        setShowUnsavedChangesDialog(true);
      } else {
        navFunction();
      }
    },
    [hasUnsavedChanges]
  );

  // Contact handlers
  const handleEditContact = (contact: Contact) => {
    setCurrentContact(contact);
    setEditContactDialogOpen(true);
  };

  const handleDeleteContact = (contact: Contact) => {
    setContactToDelete(contact);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteContact = () => {
    if (contactToDelete) {
      deleteContact.mutate({ id: contactToDelete.id });
      setContactToDelete(undefined);
    }
  };

  // Note handlers
  const handleAddNote = () => {
    setAddNoteDialogOpen(true);
  };

  const handleEditNote = (note: Note) => {
    setCurrentNote(note);
    setEditNoteDialogOpen(true);
  };

  const handleDeleteNote = (note: Note) => {
    setNoteToDelete(note);
    setConfirmNoteDialogOpen(true);
  };

  const confirmDeleteNote = () => {
    if (noteToDelete) {
      deleteNote.mutate({ id: noteToDelete.id });
      setNoteToDelete(undefined);
    }
  };


  // Redirect to dashboard if no user
  useEffect(() => {
    if (!userId) {
      navigate({ to: backTarget.path as any, replace: true });
    }
  }, [userId, navigate, from]);

  // Show loading state
  if (applicationQuery.isLoading || !userId) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4'>
        <div className='max-w-md w-full bg-white p-8 rounded-lg shadow-sm border'>
          <div className='flex justify-center mb-6'>
            <LoaderCircle className='h-12 w-12 text-indigo-600 animate-spin' />
          </div>
          <h1 className='text-xl font-semibold text-center mb-2'>
            {t('application.loading.title')}
          </h1>
          <p className='text-gray-600 text-center mb-6'>
            {t('application.loading.description')}
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (applicationQuery.isError) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4'>
        <div className='max-w-md w-full bg-white p-8 rounded-lg shadow-sm border'>
          <h1 className='text-xl font-semibold text-center mb-2 text-red-600'>
            {t('application.notFound.title')}
          </h1>
          <p className='text-gray-600 text-center mb-6'>
            {t('application.notFound.description')}
          </p>
          <button
            onClick={() => navigate({ to: backTarget.path as any })}
            className='w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors'
          >
            {t('application.notFound.returnButton')}
          </button>
        </div>
      </div>
    );
  }

  const application: Application = applicationQuery.data;

  return (
    <div className='min-h-screen bg-gray-50 p-4'>
      <div className='max-w-6xl mx-auto'>
        <ApplicationHeader
          backLabel={t(backTarget.labelKey)}
          onBackClick={() =>
            handleNavigation(() => navigate({ to: backTarget.path as any }))
          }
        />

        <div className='bg-white rounded-lg shadow-sm border p-6'>
          <Tabs defaultValue='application' className='w-full'>
            <TabsList className='mb-6 w-full justify-start'>
              <TabsTrigger value='application'>
                {t('application.tabs.application')}
              </TabsTrigger>
              <TabsTrigger
                value='contacts'
                disabled={isEditMode}
                className={isEditMode ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {t('application.tabs.contacts')}
                {!contactsQuery.isLoading &&
                  contactsQuery.data &&
                  contactsQuery.data.length > 0 &&
                  ` (${contactsQuery.data.length})`}
              </TabsTrigger>
              <TabsTrigger
                value='notes'
                disabled={isEditMode}
                className={isEditMode ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {t('application.tabs.notes')}{' '}
                {!notesQuery.isLoading &&
                  notesQuery.data &&
                  notesQuery.data.length > 0 &&
                  `(${notesQuery.data.length})`}
              </TabsTrigger>
            </TabsList>

            {/* APPLICATION TAB */}
            <TabsContent value='application' className='space-y-6'>
              {isEditMode ? (
                <ApplicationEditForm
                  editFormData={editFormData}
                  onFieldChange={handleEditFormChange}
                  onSave={handleSaveClick}
                  onCancel={handleCancelEdit}
                  isSaving={updateApplication.isPending}
                />
              ) : (
                <ApplicationViewMode
                  application={application}
                  onEditClick={handleEditClick}
                  from={from}
                />
              )}
            </TabsContent>

            {/* CONTACTS TAB */}
            <TabsContent value='contacts'>
              <ContactsTab
                contacts={contactsQuery.data || []}
                isLoading={contactsQuery.isLoading}
                error={contactsQuery.error}
                onAddContact={() => setContactDialogOpen(true)}
                onEditContact={handleEditContact}
                onDeleteContact={handleDeleteContact}
              />
            </TabsContent>

            {/* NOTES TAB */}
            <TabsContent value='notes'>
              <NotesTab
                notes={notesQuery.data || []}
                isLoading={notesQuery.isLoading}
                error={notesQuery.error}
                showSystemNotes={showSystemNotes}
                onToggleSystemNotes={() => setShowSystemNotes(!showSystemNotes)}
                onAddNote={handleAddNote}
                onEditNote={handleEditNote}
                onDeleteNote={handleDeleteNote}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialogs */}
        {application && (
          <>
            <ContactDialog
              mode='add'
              open={contactDialogOpen}
              onOpenChange={setContactDialogOpen}
              applicationId={application.id}
              existingContacts={contactsQuery.data?.length || 0}
              onSuccess={() => {
                setContactDialogOpen(false);
                utils.contacts.getByApplicationId.invalidate({
                  applicationId: application.id,
                });
              }}
            />

            <ContactDialog
              mode='edit'
              open={editContactDialogOpen}
              onOpenChange={setEditContactDialogOpen}
              applicationId={application.id}
              existingContacts={contactsQuery.data?.length || 0}
              contactToEdit={currentContact}
              onSuccess={() => {
                setEditContactDialogOpen(false);
                setCurrentContact(undefined);
                utils.contacts.getByApplicationId.invalidate({
                  applicationId: application.id,
                });
              }}
            />

            <NoteDialog
              mode='add'
              open={addNoteDialogOpen}
              onOpenChange={setAddNoteDialogOpen}
              applicationId={application.id}
              onSuccess={() => {
                setAddNoteDialogOpen(false);
                utils.notes.getByApplicationId.invalidate({
                  applicationId: application.id,
                });
              }}
            />

            <NoteDialog
              mode='edit'
              open={editNoteDialogOpen}
              onOpenChange={setEditNoteDialogOpen}
              applicationId={application.id}
              noteToEdit={currentNote}
              onSuccess={() => {
                setEditNoteDialogOpen(false);
                setCurrentNote(undefined);
                utils.notes.getByApplicationId.invalidate({
                  applicationId: application.id,
                });
              }}
            />

            <ConfirmationDialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
              title={t('contacts.deleteDialog.title')}
              description={t('contacts.deleteDialog.description', {
                name: contactToDelete?.name,
              })}
              onConfirm={confirmDeleteContact}
              confirmText={t('contacts.deleteDialog.confirmButton')}
              confirmVariant='destructive'
            />

            <ConfirmationDialog
              open={confirmNoteDialogOpen}
              onOpenChange={setConfirmNoteDialogOpen}
              title={t('notes.deleteDialog.title')}
              description={t('notes.deleteDialog.description', {
                title:
                  noteToDelete?.title || t('notes.deleteDialog.fallbackTitle'),
              })}
              onConfirm={confirmDeleteNote}
              confirmText={t('notes.deleteDialog.confirmButton')}
              confirmVariant='destructive'
            />

            <UnsavedChangesDialog
              open={showUnsavedChangesDialog}
              onSave={handleUnsavedChangesSave}
              onDiscard={handleUnsavedChangesDiscard}
            />
          </>
        )}
      </div>
    </div>
  );
}
